"use client";

import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/utils/supabase/client";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Users, Building, DollarSign, Settings, ToggleRight, Shield, Database, Zap, TrendingUp } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { showError, showSuccess } from "@/utils/toast";
import { Link } from "react-router-dom";

const FeatureFlagsTab = () => {
  const { company } = useAuth();
  const [flags, setFlags] = useState<any[]>([]);

  const { data: flagsData } = useQuery({
    queryKey: ["feature_flags", company?.id],
    queryFn: async () => {
      if (!company?.id) return [];
      const { data } = await supabase.from("feature_flags").select("*").eq("company_id", company.id);
      return data || [];
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const { error } = await supabase
        .from("feature_flags")
        .update({ enabled })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => showSuccess("Flag updated!"),
    onError: (error) => showError("Failed to update flag"),
  });

  useEffect(() => {
    setFlags(flagsData || []);
  }, [flagsData]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <ToggleRight className="h-5 w-5" />
          <span>Feature Flags</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Flag Name</TableHead>
              <TableHead>Enabled</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {flags.map((flag: any) => (
              <TableRow key={flag.id}>
                <TableCell>{flag.flag_name}</TableCell>
                <TableCell>
                  <Switch
                    checked={flag.enabled}
                    onCheckedChange={(enabled) => toggleMutation.mutate({ id: flag.id, enabled })}
                  />
                </TableCell>
                <TableCell>{flag.company_id ? "Company-Specific" : "Global"}</TableCell>
                <TableCell>
                  <Button variant="outline" size="sm">Edit</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {flags.length === 0 && <p className="text-muted-foreground text-center py-8">No feature flags configured yet.</p>}
      </CardContent>
    </Card>
  );
};

const UsersTab = () => {
  const { company } = useAuth();
  const { data: users } = useQuery({
    queryKey: ["admin_users", company?.id],
    queryFn: async () => {
      if (!company?.id) return [];
      const { data } = await supabase.from("profiles").select("*").eq("company_id", company.id);
      return data || [];
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Users className="h-5 w-5" />
          <span>Users</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users?.map((user: any) => (
              <TableRow key={user.id}>
                <TableCell>{user.first_name} {user.last_name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell><Badge>{user.role}</Badge></TableCell>
                <TableCell>
                  <Button variant="outline" size="sm">Edit</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

const CompaniesTab = () => {
  const { isSuperAdmin } = useAuth();
  const { data: companies } = useQuery({
    queryKey: ["admin_companies"],
    queryFn: async () => {
      if (!isSuperAdmin) return [];
      const { data } = await supabase.from("companies").select("*");
      return data || [];
    },
    enabled: isSuperAdmin,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Building className="h-5 w-5" />
          <span>Companies</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Subdomain</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {companies?.map((company: any) => (
              <TableRow key={company.id}>
                <TableCell>{company.name}</TableCell>
                <TableCell>{company.subdomain}</TableCell>
                <TableCell><Badge>{company.status}</Badge></TableCell>
                <TableCell>
                  <Button variant="outline" size="sm">Edit</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

const BillingTab = () => {
  const { isSuperAdmin } = useAuth();
  const { data: billing } = useQuery({
    queryKey: ["admin_billing"],
    queryFn: async () => {
      if (!isSuperAdmin) return [];
      const { data } = await supabase.from("subscriptions").select("*");
      return data || [];
    },
    enabled: isSuperAdmin,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <DollarSign className="h-5 w-5" />
          <span>Billing Overview</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {billing?.map((sub: any) => (
              <TableRow key={sub.id}>
                <TableCell>{sub.company_id}</TableCell>
                <TableCell>{sub.plan}</TableCell>
                <TableCell><Badge>{sub.status}</Badge></TableCell>
                <TableCell>£{sub.prorated_amount / 100}</TableCell>
                <TableCell>
                  <Button variant="outline" size="sm">View</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

const AdminPage = () => {
  const { isAdmin, isSuperAdmin } = useAuth();

  if (!isAdmin && !isSuperAdmin) {
    return (
      <div className="container mx-auto p-4">
        <Alert variant="destructive">
          <AlertDescription>Access denied. Admins only.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <Tabs defaultValue="users" className="w-full">
        <TabsList>
          <TabsTrigger value="users">
            <Users className="mr-2 h-4 w-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="companies">
            <Building className="mr-2 h-4 w-4" />
            Companies
          </TabsTrigger>
          <TabsTrigger value="billing">
            <DollarSign className="mr-2 h-4 w-4" />
            Billing
          </TabsTrigger>
          <TabsTrigger value="settings">
            <Settings className="mr-2 h-4 w-4" />
            Settings
          </TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="mt-6">
          <UsersTab />
        </TabsContent>
        <TabsContent value="companies" className="mt-6">
          <CompaniesTab />
        </TabsContent>
        <TabsContent value="billing" className="mt-6">
          <BillingTab />
        </TabsContent>
        <TabsContent value="settings" className="mt-6">
          <FeatureFlagsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPage;